import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import RefreshOutlinedIcon from '@material-ui/icons/RefreshOutlined';
import SwapHorizOutlinedIcon from '@material-ui/icons/SwapHorizOutlined';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';

import { Layout } from '../../layouts/private/Private';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import Title from '../../components/Title/Title';
import SimpleDialog from '../../components/Dialog/Dialog';
import { ToastCF } from '../../components/Toast/Toast';
import PainelSessaoDigitacao, { MODOS_SESSAO } from './PainelSessaoDigitacao';
import DialogSelecionarRegiao from './DialogSelecionarRegiao';
import { ButtonPrimary, ButtonDefault, ButtonDanger } from '../../components/Button/Button';
import useSessaoDigitacao from '../../hooks/useSessaoDigitacao';
import { PREFETCH_RESERVA } from '../../storage/DigitacaoReservaStorage';
import DigitacaoRegiaoStorage from '../../storage/DigitacaoRegiaoStorage';
import { isAdministradorOuCentral } from '../../security/AuthorizedFunction';
import { patternEnum } from '../../variables/Enums/Enum';
import EnumService from '../../services/Enum';

const PATH_DIGITACAO = '/admin/digitacao';
const SEGUNDOS_FILA_VAZIA = 10;

export const CONFIGURACAO_SESSAO = {
    [MODOS_SESSAO.DIGITAR]: {
        titulo: 'Digitar Marcações',
        carregando: 'Carregando próxima prova...',
        filaVaziaTitulo: 'Não há provas para a sua região',
        filaVazia: 'Aguarde novas provas da sua região entrarem na fila de digitação.',
        registro: 'Digitação registrada.',
        divergencia: 'Divergência — enviada para revisão',
        avisoSaida: 'Há marcações não enviadas nesta prova. Se sair agora, elas serão descartadas.',
        salvamentoPendente: 'Sem conexão com o servidor. A digitação foi salva localmente e será enviada automaticamente.',
        salvamentoConcluido: quantidade => `${quantidade} ${quantidade === 1 ? 'digitação pendente enviada' : 'digitações pendentes enviadas'} com sucesso.`,
    },
    [MODOS_SESSAO.REVISAR]: {
        titulo: 'Revisar Marcações',
        carregando: 'Carregando próxima prova para revisão...',
        filaVaziaTitulo: 'Não há provas na fila',
        filaVazia: 'Aguarde novas provas entrarem na fila de revisão.',
        registro: 'Revisão registrada.',
        divergencia: 'Divergência entre a nota digitada e a revisada',
        avisoSaida: 'Há marcações não enviadas nesta revisão. Se sair agora, elas serão descartadas.',
        salvamentoPendente: 'Sem conexão com o servidor. A revisão foi salva localmente e será enviada automaticamente.',
        salvamentoConcluido: quantidade => `${quantidade} ${quantidade === 1 ? 'revisão pendente enviada' : 'revisões pendentes enviadas'} com sucesso.`,
    },
};

const useStyles = makeStyles(theme => ({
    sessao: {
        width: '100%',
    },
    conteudo: {
        width: '70%',
        margin: '0 auto',
    },
    barra: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    barraInfo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: theme.spacing(1.5),
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: theme.spacing(0.5, 1.5),
        backgroundColor: theme.palette.grey[200],
        color: '#000',
        borderRadius: 4,
    },
    botaoFinalizar: {
        marginRight: theme.spacing(3),
    },
    alertaPendentes: {
        marginLeft: theme.spacing(1),
        verticalAlign: 'middle',
        color: theme.palette.warning.main,
    },
    carregando: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(8),
    },
    carregandoTexto: {
        marginTop: theme.spacing(2),
    },
    vazio: {
        padding: theme.spacing(5),
        textAlign: 'center',
    },
    vazioAcoes: {
        marginTop: theme.spacing(2),
    },
    contagem: {
        marginTop: theme.spacing(1),
    },
}));

const SessaoDigitacaoBase = ({ mode = MODOS_SESSAO.DIGITAR }) => {
    const classes = useStyles();
    const history = useHistory();

    const config = CONFIGURACAO_SESSAO[mode] || CONFIGURACAO_SESSAO[MODOS_SESSAO.DIGITAR];

    const permiteEscolherRegiao = mode === MODOS_SESSAO.DIGITAR && isAdministradorOuCentral();

    const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
    const [statusOptions, setStatusOptions] = useState([]);
    const [sujo, setSujo] = useState(false);
    const [confirmarSaida, setConfirmarSaida] = useState(false);
    const [segundosFilaVazia, setSegundosFilaVazia] = useState(SEGUNDOS_FILA_VAZIA);
    const [regiao, setRegiao] = useState(() => (permiteEscolherRegiao ? DigitacaoRegiaoStorage.obter() : null));
    const [dialogRegiaoAberto, setDialogRegiaoAberto] = useState(false);

    const precisaSelecionarRegiao = permiteEscolherRegiao && !regiao;

    const selecionarRegiao = useCallback(novaRegiao => {
        DigitacaoRegiaoStorage.salvar(novaRegiao);
        setRegiao(novaRegiao);
        setDialogRegiaoAberto(false);
    }, []);

    const notificar = useCallback((message, type = 'error') => {
        setToast({ open: true, message, type });
    }, []);

    useEffect(() => {
        EnumService.listarStatusProva()
            .then(res => setStatusOptions(res.data || []))
            .catch(() => {});
    }, []);

    const aoRegistrado = useCallback(dto => {
        if (!dto) return;

        const partes = [];

        if (dto.notaCalculadaNova !== null && dto.notaCalculadaNova !== undefined) {
            partes.push(`Nota: ${dto.notaCalculadaNova}`);
        }
        if (dto.statusFinal) {
            partes.push(`Status: ${patternEnum(dto.statusFinal, statusOptions)}`);
        }
        if (dto.divergenciaNota) {
            partes.push(config.divergencia);
        }

        notificar(partes.length ? partes.join(' | ') : config.registro, 'success');
    }, [notificar, statusOptions, config]);

    const aoErro = useCallback((message, type) => notificar(message, type), [notificar]);

    const aoSalvamentoPendente = useCallback(() => notificar(config.salvamentoPendente, 'warning'), [notificar, config]);

    const aoSalvamentoConcluido = useCallback(quantidade => notificar(config.salvamentoConcluido(quantidade), 'success'), [notificar, config]);

    const {
        prova,
        carregando,
        enviando,
        filaVazia,
        erro,
        reservaLocal,
        digitadasNaSessao,
        enviosPendentes,
        carregarProxima,
        registrar,
        invalidar,
        pular,
    } = useSessaoDigitacao({
        mode,
        regiao: permiteEscolherRegiao ? regiao : null,
        onRegistrado: aoRegistrado,
        onErro: aoErro,
        onSalvamentoPendente: aoSalvamentoPendente,
        onSalvamentoConcluido: aoSalvamentoConcluido,
    });

    const reservaExpirou = useCallback(() => {
        notificar('Tempo de reserva esgotado. Carregando a próxima prova.', 'warning');
        carregarProxima();
    }, [notificar, carregarProxima]);

    useEffect(() => {
        if (precisaSelecionarRegiao || !filaVazia || carregando || erro) return undefined;

        let restantes = SEGUNDOS_FILA_VAZIA;
        setSegundosFilaVazia(restantes);

        const intervalo = setInterval(() => {
            restantes -= 1;
            setSegundosFilaVazia(restantes);

            if (restantes <= 0) {
                clearInterval(intervalo);
                carregarProxima();
            }
        }, 1000);

        return () => clearInterval(intervalo);
    }, [precisaSelecionarRegiao, filaVazia, carregando, erro, carregarProxima]);

    const filaVaziaTitulo = permiteEscolherRegiao && regiao ? `Não há provas para a região ${regiao}` : config.filaVaziaTitulo;
    const filaVaziaTexto = permiteEscolherRegiao && regiao
        ? `Aguarde novas provas da região ${regiao} entrarem na fila de digitação.`
        : config.filaVazia;

    const sair = () => {
        if (sujo) {
            setConfirmarSaida(true);
            return;
        }

        history.push(PATH_DIGITACAO);
    };

    const confirmarSaidaSim = () => {
        setConfirmarSaida(false);
        history.push(PATH_DIGITACAO);
    };

    const links = [{ path: PATH_DIGITACAO, name: 'Digitação' }];

    const acoesSaida = (
        <>
            <ButtonPrimary
                name="Finalizar"
                startIcon={<CheckOutlinedIcon />}
                onClick={confirmarSaidaSim}
                style={{ margin: 4 }}
            />
            <ButtonDefault
                name="Continuar"
                startIcon={<ClearOutlinedIcon />}
                onClick={() => setConfirmarSaida(false)}
                style={{ margin: 4 }}
            />
        </>
    );

    return (
        <Layout>
            <ToastCF
                open={toast.open}
                message={toast.message}
                type={toast.type}
                handleClose={() => setToast(anterior => ({ ...anterior, open: false }))}
            />

            <div className={classes.sessao}>
                <Grid container>
                    <Grid item xs={12}>
                        <Breadcrumbs links={links} active={config.titulo} />
                    </Grid>
                </Grid>

                <div className={classes.barra}>
                    <Title>{config.titulo}</Title>
                    <div className={classes.barraInfo}>
                        {permiteEscolherRegiao &&
                            <div className={classes.badge}>
                                <Typography variant="body2">
                                    Região: <b>{regiao || '—'}</b>
                                </Typography>
                                <ButtonDefault
                                    name="Trocar"
                                    startIcon={<SwapHorizOutlinedIcon />}
                                    onClick={() => setDialogRegiaoAberto(true)}
                                    disabled={enviando}
                                    style={{ marginLeft: 8 }}
                                />
                            </div>
                        }
                        <div className={classes.badge}>
                            <Typography variant="body2">
                                Provas Reservadas: <b>{reservaLocal}</b>
                            </Typography>
                        </div>
                        <div className={classes.badge}>
                            <Typography variant="body2">
                                Marcações Digitadas na Sessão Atual: <b>{digitadasNaSessao}</b>
                            </Typography>
                        </div>
                        <div className={classes.badge}>
                            <Typography variant="body2">
                                Aguardando Envio: <b>{enviosPendentes}</b>
                                {enviosPendentes > PREFETCH_RESERVA && (
                                    <Tooltip title="Há mais marcações aguardando envio do que o limite de reserva. Verifique sua conexão.">
                                        <WarningOutlinedIcon className={classes.alertaPendentes} fontSize="small" />
                                    </Tooltip>
                                )}
                            </Typography>
                        </div>
                    </div>
                    <ButtonDanger
                        className={classes.botaoFinalizar}
                        name="Finalizar Sessão"
                        startIcon={<ClearOutlinedIcon />}
                        onClick={sair}
                        disabled={enviando}
                    />
                </div>

                <div className={classes.conteudo}>
                    {!precisaSelecionarRegiao && carregando &&
                        <div className={classes.carregando}>
                            <CircularProgress />
                            <Typography variant="body2" color="textSecondary" className={classes.carregandoTexto}>
                                {config.carregando}
                            </Typography>
                        </div>
                    }

                    {!precisaSelecionarRegiao && !carregando && erro &&
                        <Paper className={classes.vazio} variant="outlined">
                            <Typography variant="h6" color="error" gutterBottom>Não foi possível carregar a fila</Typography>
                            <Typography variant="body2" color="textSecondary">{erro}</Typography>
                            <div className={classes.vazioAcoes}>
                                <ButtonPrimary name="Tentar novamente" startIcon={<RefreshOutlinedIcon />} onClick={carregarProxima} />
                            </div>
                        </Paper>
                    }

                    {!precisaSelecionarRegiao && !carregando && !erro && filaVazia &&
                        <Paper className={classes.vazio} variant="outlined">
                            <Typography variant="h6" gutterBottom>{filaVaziaTitulo}</Typography>
                            <Typography variant="body2" color="textSecondary">
                                {filaVaziaTexto}
                            </Typography>
                            <Typography variant="body2" className={classes.contagem} color="textSecondary">
                                Buscando novas provas em <b>{segundosFilaVazia}</b> segundos...
                            </Typography>
                            <div className={classes.vazioAcoes}>
                                <ButtonPrimary name="Verificar novamente" startIcon={<RefreshOutlinedIcon />} onClick={carregarProxima} />
                            </div>
                        </Paper>
                    }

                    {!precisaSelecionarRegiao && !carregando && !erro && !filaVazia && prova &&
                        <PainelSessaoDigitacao
                            prova={prova}
                            loading={enviando}
                            onRegistrar={registrar}
                            onInvalidar={invalidar}
                            onPular={pular}
                            onDirtyChange={setSujo}
                            onReservaExpirar={reservaExpirou}
                        />
                    }
                </div>
            </div>

            <SimpleDialog
                openDialog={confirmarSaida}
                title="Finalizar Sessão?"
                closeDialog={() => setConfirmarSaida(false)}
                actions={acoesSaida}
                fullWidth={true}
                maxWidth="sm"
            >
                {config.avisoSaida}
            </SimpleDialog>

            {permiteEscolherRegiao &&
                <DialogSelecionarRegiao
                    open={precisaSelecionarRegiao || dialogRegiaoAberto}
                    obrigatorio={precisaSelecionarRegiao}
                    regiaoAtual={regiao}
                    onConfirmar={selecionarRegiao}
                    onCancelar={() => setDialogRegiaoAberto(false)}
                />
            }
        </Layout>
    );
};

export default SessaoDigitacaoBase;
